/**
 * Platform and multi-tenant host lists — a leaf module with no imports, so `brand-roster.ts` (which
 * builds its noise filter from them) and `src/lib/brands/identity.ts` (which refuses to call a
 * multi-tenant host a brand's own domain) can both read them without importing each other.
 */

/**
 * Source platforms that `detected_brands` picks up as "brands" — review
 * directories, UGC/community sites and publishers. They are where the answer
 * came FROM, not competitors in the category, and they crowd the real roster
 * out of a 10-row scoreboard.
 *
 * Deliberately limited to platforms whose business IS being a source. Real
 * vendors that merely happen to run big content operations (HubSpot, Salesforce,
 * Intuit, …) are NOT listed: for a CRM project HubSpot is the competitor, and
 * hardcoding it here would silently delete the most important row on the board.
 * Those are handled contextually instead, by the LLM's per-answer
 * `relationship: 'platform'` classification.
 *
 * A brand is never filtered when it is the project's own brand or a tracked
 * competitor — an explicit user decision outranks this list.
 *
 * Declared as domains; the key set in `brand-roster.ts` is derived from them.
 */
export const PLATFORM_SOURCE_DOMAINS: readonly string[] = [
  // Review directories / marketplaces
  'g2.com',
  'g2crowd.com',
  'capterra.com',
  'getapp.com',
  'softwareadvice.com',
  'sourceforge.net',
  'trustradius.com',
  'trustpilot.com',
  'gartner.com',
  'crunchbase.com',
  'producthunt.com',
  'alternativeto.net',
  'slashdot.org',
  // Community / UGC
  'reddit.com',
  'youtube.com',
  'quora.com',
  'linkedin.com',
  'medium.com',
  'substack.com',
  'github.com',
  'stackoverflow.com',
  'ycombinator.com',
  'facebook.com',
  'twitter.com',
  'x.com',
  'discord.com',
  'wikipedia.org',
  // Publishers that recur as citation sources
  'techradar.com',
  'forbes.com',
  'zdnet.com',
  'pcmag.com',
  'cnet.com',
]

/**
 * Domains that HOST many independent brands. Detection routinely reports a
 * WordPress plugin as `{ name: 'Strong Testimonials', domain: 'wordpress.org' }`
 * — keying that on the domain would fuse every plugin in the market into one
 * scoreboard row (observed on real data before this guard existed).
 *
 * Every platform above is also a multi-tenant host; these are the extras that
 * are hosts without being citation sources in their own right.
 */
export const MULTI_TENANT_EXTRA_DOMAINS: readonly string[] = [
  'wordpress.org',
  'wordpress.com',
  'wix.com',
  'webflow.com',
  'squarespace.com',
  'shopify.com',
  'gumroad.com',
  'appsumo.com',
  'notion.so',
  'apps.apple.com',
  'play.google.com',
  'chromewebstore.google.com',
]
